<template lang="pug">
	.carto-map-charts.widgets-area.flex-col.align-center.center
		Loader(v-if="!loaded", :text="$tCaption('Loading') + '..'")
		template(v-if="loaded")
			.widgets-row
				.widgets-col
					.widget.clickable(@click="autoStyleFieldWidgetChanged('a_pop_num', 'population')", :class="{ 'auto-styled': autoStyleField === 'a_pop_num' }")
						.header {{$tCaption('Population')}}
						.data-statistic {{formatNumber(totals.populationSum)}}
						.auto-styled-icon
							i.fas.fa-map-marker-alt
					.widget.clickable(@click="autoStyleFieldWidgetChanged('a_med_in_1', 'average income')", :class="{ 'auto-styled': autoStyleField === 'a_med_in_1' }")
						.header {{$tCaption('Average income')}}
						.data-statistic ${{formatNumber(totals.avg_income || totals.a_med_in_1)}}
						.auto-styled-icon
							i.fas.fa-map-marker-alt
					.widget.clickable(@click="autoStyleFieldWidgetChanged('a_med_valu', 'average home value')", :class="{ 'auto-styled': autoStyleField === 'a_med_valu' }")
						.header {{$tCaption('Average home value')}}
						.data-statistic ${{formatNumber(totals.avg_home_value || totals.a_med_valu)}}
						.auto-styled-icon
							i.fas.fa-map-marker-alt
				.widgets-col
					.widget.chart
						.header {{$tCaption('Median income')}}
						.data
							DxChart(ref="chart1", v-if="totals.medianIncome && totals.medianIncome.length", :data-source="totals.medianIncome", :size="{ height: 75, width: '100%' }", :palette="[accentColor]", @point-click="autoStyleFieldWidgetChanged('a_med_in_1', 'median income')")
								DxSeries(argument-field="start", value-field="freq", type="bar")
								DxLegend(:visible="false")
								DxCommonSeriesSettings(type="bar")
								DxTooltip(:enabled="true", :zIndex="100000", :color="subColor", :customizeTooltip="(e) => ({ text: `${$tCaption('Population')}: ${e.valueText}\n$${formatNumber(e.argument)}` })")
								DxValueAxis(:visible="false", :color="subColor", :grid="{ color: subColor }", :tick="{ color: '#00000000' }", :label="{ font: { color: accentColor }}")
								DxArgumentAxis(:visible="true", :color="subColor", :grid="{ color: subColor }", :label="{ font: { color: accentColor }, format: { type: 'thousands' } }", :tick="{ color: accentColor }")
							span(v-else) ${{formatNumber(totals.a_med_in_1)}}
					.widget.chart
						.header {{$tCaption('Education level')}}
						.data
							DxChart(ref="chart7", :data-source="educationData",
								:size="{ height: compact ? 145 : 75, width: '100%' }", :palette="[accentColor]", :rotated="true",
								:customizePoint="(v) => v.value < educationAverage ? { color: subColor } : { color: accentColor }",
								@point-click="(e) => onPointClick(e, 'Education level')"
							)
								DxSeries(argument-field="name", value-field="value", :barWidth="20")
								DxLegend(:visible="false")
								DxCommonSeriesSettings(type="bar", argument-field="name", value-field="value", :barWidth="20")
								DxTooltip(:enabled="true", :zIndex="100000", :color="subColor", :format="format")
								DxValueAxis(:visible="false", :color="subColor", :grid="{ color: subColor }", :tick="{ color: '#00000000' }", :label="{ font: { color: accentColor }, format: format }")
								DxArgumentAxis(:visible="true", :color="subColor", :grid="{ color: subColor }", :label="{ font: { color: accentColor }}", :tick="{ color: accentColor }")
				.widget.chart
					.header {{$tCaption('Age')}}
					.data
						DxChart(ref="chart2", :data-source="ageData",
							:size="{ height: 200, width: '100%' }", :palette="[accentColor]", :rotated="true",
							:customizePoint="(v) => v.value < ageAverage ? { color: subColor } : { color: accentColor }",
							@point-click="(e) => onPointClick(e, 'age')"
						)
							DxSeries(argument-field="name", value-field="value", :barWidth="20")
							DxLegend(:visible="false")
							DxCommonSeriesSettings(type="bar", argument-field="field", value-field="value", :barWidth="20", selection-mode="allArgumentPoints")
							DxTooltip(:enabled="true", :zIndex="100000", :color="subColor", :format="format")
							DxValueAxis(:visible="false", :color="subColor", :grid="{ color: subColor }", :tick="{ color: '#00000000' }", :label="{ font: { color: accentColor }}")
							DxArgumentAxis(:visible="true", :color="subColor", :grid="{ color: subColor }", :label="{ font: { color: accentColor }}", :tick="{ color: accentColor }")
				.widget.chart
					.header {{$tCaption('Ethnicity')}}
					.data
						DxPieChart(ref="chart3", :data-source="ethData",
							resolve-label-overlapping="shift",
							type="doughnut",
							:size="{ height: 180, width: '100%' }", :rotated="true",
							:series="[pieChartSerieConfig('ethnicity')]",
							:legend="{ visible: false}",
							:inner-radius="0.65",
							:palette="[accentColor, '#ecda9a', '#f1b973', '#f7945d', '#f86f56', '#ee4d5a']",
							center-template="centerTemplate",
							@point-click="(e) => onPointClick(e, 'ethnicity')"
						)
							DxTooltip(:enabled="true", :zIndex="100000", :color="subColor", :format="format", :customizeTooltip="(e) => ({ text: `${e.valueText}\n${e.argument}` })")
							template(v-slot:centerTemplate="center")
								svg
									text(text-anchor="middle"
										x="100"
										y="120"
									)
										tspan(:style="{fontSize: 50, fontName: 'Roboto', fontWeight: 100, fill: accentColor, letterSpacing: '-4px' }", x="100") {{max(ethData).percent}}%
										tspan(:style="{fontSize: 20, fontName: 'Roboto', fontWeight: 100, fill: accentColor }", x="100", dy="20px") {{max(ethData).name}}
				.widget.chart
					.header {{$tCaption('Children in household')}}
					.data
						DxPieChart(ref="chart4", :data-source="kidsData",
							resolve-label-overlapping="shift",
							type="doughnut",
							:size="{ height: 180, width: '100%' }", :rotated="true",
							:series="[pieChartSerieConfig('children')]",
							:legend="{ visible: false}",
							:inner-radius="0.65",
							:palette="[accentColor, subColor]",
							center-template="centerTemplate",
							@point-click="(e) => onPointClick(e, 'children in household')"
						)
							DxTooltip(:enabled="true", :zIndex="100000", :color="subColor", :format="format", :customizeTooltip="(e) => ({ text: `${e.valueText}\n${e.argument}` })")
							template(v-slot:centerTemplate="center")
								svg
									text(text-anchor="middle"
										x="100"
										y="120"
									)
										tspan(:style="{fontSize: 50, fontName: 'Roboto', fontWeight: 100, fill: accentColor, letterSpacing: '-4px' }", x="100") {{max(kidsData).percent}}%
										tspan(:style="{fontSize: 20, fontName: 'Roboto', fontWeight: 100, fill: accentColor }", x="100", dy="20px") {{max(kidsData).name}}
				.widget.chart
					.header {{$tCaption('Household income')}}
					.data
						DxChart(ref="chart5", :data-source="incomeData",
							:size="{ height: 180, width: '100%' }", :palette="[accentColor]", :rotated="true",
							:customizePoint="(v) => v.value < incomeAverage ? { color: subColor } : { color: accentColor }",
							@point-click="(e) => onPointClick(e, 'household income')"
						)
							DxSeries(argument-field="name", value-field="value", :barWidth="20")
							DxLegend(:visible="false")
							DxCommonSeriesSettings(type="bar", argument-field="name", value-field="value", :barWidth="20")
							DxTooltip(:enabled="true", :zIndex="100000", :color="subColor", :format="format")
							DxValueAxis(:visible="false", :color="subColor", :grid="{ color: subColor }", :tick="{ color: '#00000000' }", :label="{ font: { color: accentColor }}")
							DxArgumentAxis(:visible="true", :color="subColor", :grid="{ color: subColor }", :label="{ font: { color: accentColor }}", :tick="{ color: accentColor }")
				.widget.chart
					.header {{$tCaption('Gender')}}
					.data
						DxPieChart(ref="chart6", :data-source="genderData",
							resolve-label-overlapping="shift",
							type="doughnut",
							:size="{ height: 180, width: '100%' }", :rotated="true",
							:series="[pieChartSerieConfig('gender')]",
							:legend="{ visible: false}",
							:inner-radius="0.65",
							:palette="[accentColor, subColor]",
							center-template="centerTemplate",
							@point-click="(e) => onPointClick(e, 'gender')"
						)
							DxTooltip(:enabled="true", :zIndex="100000", :color="subColor", :format="format", :customizeTooltip="(e) => ({ text: `${e.valueText}\n${e.argument}` })")
							template(v-slot:centerTemplate="center")
								svg
									text(text-anchor="middle"
										x="100"
										y="120"
									)
										tspan(:style="{fontSize: 50, fontName: 'Roboto', fontWeight: 100, fill: accentColor, letterSpacing: '-4px' }", x="100") {{max(genderData).percent}}%
										tspan(:style="{fontSize: 20, fontName: 'Roboto', fontWeight: 100, fill: accentColor }", x="100", dy="20px") {{max(genderData).name}}
				.widget(v-if="totals.locationName")
					.header {{$tCaption('Location')}}
					.data-statistic {{totals.locationName}}
					.auto-styled-icon
						i.fas.fa-map-marker-alt
</template>

<script>
	import _ from 'lodash';
	import VueTypes from 'vue-types';
	import {DxChart, DxSeries, DxLegend, DxSize, DxCommonSeriesSettings, DxTooltip, DxValueAxis, DxArgumentAxis, DxGrid, DxLabel, DxVisualRange, DxWholeRange, DxSeriesTemplate, DxFormat} from 'devextreme-vue/chart';
	import DxPieChart from 'devextreme-vue/pie-chart';
	import { DxResponsiveBox, DxItem, DxLocation, DxCol, DxRow } from 'devextreme-vue/responsive-box';
	import Data from 'app-core/Data';
	import Loader from 'app-core/Loader.vue';

	export default {
		translationCategory: 'carto.CartoMapCharts',
		name: 'carto-map-charts',
		components: {DxChart,
			DxSeries,
			DxLegend,
			DxSize,
			DxCommonSeriesSettings,
			DxTooltip, DxValueAxis,
			DxArgumentAxis,
			DxGrid,
			DxLabel,
			DxVisualRange,
			DxWholeRange,
			DxSeriesTemplate,
			DxFormat,
			DxResponsiveBox,
			DxItem,
			DxLocation,
			DxCol,
			DxRow,
			DxPieChart,
			Loader
		},
		props: {
			totals: VueTypes.object.def({}),
			compact: VueTypes.bool.def(false),
			accentColor: VueTypes.string.def('#dca0ff'),
			subColor: VueTypes.string.def('#dca0ff33'),
			autoStyleField: VueTypes.string.def(null),
			format: VueTypes.string.def('thousands')
		},
		data() {
			const $this = this;
			return {
				screen(width) {
					return (width < 900) || $this.compact ? 'xs' : 'lg';
				}
			};
		},
		computed: {
			ageData() {
				return [
					{ name: '< 18', value: this.totals.a_age_unde, field: 'a_age_unde' },
					{ name: '18 - 24', value: this.totals.a_age_18_2, field: 'a_age_18_2' },
					{ name: '25 - 34', value: this.totals.a_age_25_3, field: 'a_age_25_3' },
					{ name: '35 - 44', value: this.totals.a_age_35_4, field: 'a_age_35_4' },
					{ name: '45 - 54', value: this.totals.a_age_45_5, field: 'a_age_45_5' },
					{ name: '55 - 64', value: this.totals.a_age_55_6, field: 'a_age_55_6' },
					{ name: '65 +', value: this.totals.a_age_over, field: 'a_age_over' }
				].reverse();
			},
			ageAverage() {
				return parseFloat(_.sumBy(this.ageData, 'value')) / this.ageData.length;
			},
			incomeData() {
				var sign = Data.currencySign();
				var inData = [
					{ name: `< ${sign}50K`, value: this.totals.a_under_50, field: 'a_under_50' },
					{ name: `${sign}50K - ${sign}100K`, value: this.totals.a_50k_100k, field: 'a_50k_100k' },
					{ name: `${sign}100K - ${sign}150K`, value: this.totals.a_100k_150, field: 'a_100k_150' },
					{ name: `${sign}150K +`, value: this.totals.a_over_150, field: 'a_over_150' }
				].reverse();
				return inData;
			},
			incomeAverage() {
				return this.avg(this.incomeData);
			},
			educationData() {
				return [
					{ name: this.$tCaption('No college'), value: this.totals.a_no_colle, field: 'a_no_colle' },
					{ name: this.$tCaption('College'), value: this.totals.a_college, field: 'a_college' },
					{ name: this.$tCaption('Grad school'), value: this.totals.a_graduate, field: 'a_graduate' }
				].reverse();
			},
			educationAverage() {
				return parseFloat(_.sumBy(this.educationData, 'value')) / this.educationData.length;
			},
			kidsData() {
				return [
					{ name: this.$tCaption('No kids'), value: this.totals.a_no_kid, field: 'a_no_kid' },
					{ name: this.$tCaption('Has kids'), value: this.totals.a_with_k_1, field: 'a_with_k_1' }
				].sort((a,b) => b.value - a.value);
			},
			ethData() {
				return [
					{ name: this.$tCaption('Cauc.'), value: this.totals.a_white, field: 'a_white' },
					{ name: this.$tCaption('Af. amer.'), value: this.totals.a_black, field: 'a_black' },
					{ name: this.$tCaption('Asian'), value: this.totals.a_asian, field: 'a_asian' },
					{ name: this.$tCaption('Hisp.'), value: this.totals.a_hispanic, field: 'a_hispanic' },
					{ name: this.$tCaption('Other'), value: this.totals.a_other, field: 'a_other' }
				].sort((a,b) => b.value - a.value);
			},
			genderData() {
				return [
					{ name: this.$tCaption('Male'), value: this.totals.a_male, field: 'a_male' },
					{ name: this.$tCaption('Female'), value: this.totals.a_female, field: 'a_female' }
				].sort((a,b) => b.value - a.value);
			},
			loaded() {
				return !!this.totals?.loaded;
			}
		},
		mounted() {
			if (__DEBUG__)
				window.cartoMapCharts = this;
		},
		methods: {
			pieChartSerieConfig(type) {
				return {
					argumentField: 'name',
					valueField: 'value',
					label: {
						visible: !type,
						backgroundColor: 'none',
						format: 'fixedPoint',
						connector: {
							visible: true
						},
						customizeText: ({ argumentText, valueText, percentText }) => `${argumentText}`
					}
				};
			},
			avg(source) {
				return parseFloat(_.sumBy(source, 'value')) / source.length;
			},
			max(source) {
				var item = _.maxBy(source, 'value') || {};
				var total = _.sumBy(source, 'value') || 0;
				if (!total) return 0;
				return {...item, percent: Math.floor(parseFloat(item.value * 100) / total) || 0};
			},
			formatNumber(num) {
				if (isNaN(num)) return 0;
				return Data.formatNumber(Math.floor(num), 'fixedPoint');
			},
			autoStyleFieldWidgetChanged(field, autoStyleName) {
				this.clearSelection();
				this.$emit('auto-style-field-changed', field, autoStyleName);
			},
			clearSelection() {
				for(var ref in this.$refs) {
					if (ref.indexOf('chart') > -1) {
						this.$refs[ref].instance && this.$refs[ref].instance.clearSelection && this.$refs[ref].instance.clearSelection();
					}
				}
			},
			onPointClick(e, autoStyleName) {
				if (this.compact) return;
				this.autoStyleFieldWidgetChanged(e.target.data.field, autoStyleName);
				e.target.select();
			}
		}
	};
</script>
<style lang='scss'>
	.carto-map-charts {
		@import "main/themes/generated/variables.base.scss";

		.loading {
			padding: 20px;
			color: rgba(160, 160, 160, 0.85);

			> .loader {
				span {
					margin-left: 10px;
				}
			}
		}

		.widgets-row {
			justify-content: center;
		}

		.widgets-area {
			background: #57416b;
		}

		.widget {
			background: #d584ff30;
			border-radius: 2px;
			position: relative;

			.header {
				color: $base-accent;
			}

			.data-statistic {
				color: #dca0ff;
			}

			&.clickable {
				cursor: pointer;
				transition: 0.3s;

				&:hover {
					background: $base-accent;

					.header {
						color: $base-bg-transparent;
					}

					.data-statistic {
						color: $base-bg;
					}
				}
			}

			> .auto-styled-icon {
				position: absolute;
				right: 10px;
				top: 10px;
				color: $base-accent;
			}

			&:not(.auto-styled) {
				.auto-styled-icon {
					display: none;
				}
			}

			&.chart {
				min-width: 200px;
			}
		}
	}
</style>
